import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { ProjectMilestone } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';

// "Suggest milestones" / "Suggest tasks": Claude reads the project's
// scope of work and proposes a list. Two separate buttons, because one
// plan with tasks folded under milestones made it hard to see which
// tasks were going in.
//
// The proposal is only a draft on screen - every row has a checkbox and
// can be retyped, and nothing reaches the project until Add, which writes
// the ticked rows in one transaction. Milestone and task names show on
// the client's portal, so a person reads them first.

type Kind = 'milestones' | 'tasks';

interface MilestoneRow {
  key: string;
  keep: boolean;
  title: string;
  milestoneDate: string;
}

interface TaskRow {
  key: string;
  keep: boolean;
  title: string;
  milestoneId: string | null;
}

type Suggestion =
  | { kind: 'milestones'; milestones: Array<{ title: string; milestoneDate: string }>; notes: string }
  | { kind: 'tasks'; tasks: Array<{ title: string; milestoneId: string | null }>; notes: string };

const inputClass =
  'block rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-1.5 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

export function ProjectPlanSuggest({
  projectId,
  kind,
  milestones,
  onAdded,
}: {
  projectId: string;
  kind: Kind;
  /** The project's milestones, to place suggested tasks under. */
  milestones: ProjectMilestone[];
  onAdded: () => Promise<void> | void;
}): React.ReactElement {
  const [busy, setBusy] = useState<'suggesting' | 'adding' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [milestoneRows, setMilestoneRows] = useState<MilestoneRow[] | null>(null);
  const [taskRows, setTaskRows] = useState<TaskRow[] | null>(null);
  const [notes, setNotes] = useState('');
  const open = kind === 'milestones' ? milestoneRows !== null : taskRows !== null;

  async function suggest() {
    setBusy('suggesting');
    setError(null);
    try {
      const res = await apiFetch<Suggestion>(`/api/projects/${projectId}/plan-suggestion`, {
        method: 'POST',
        json: { kind },
      });
      if (res.kind === 'milestones') {
        setMilestoneRows(res.milestones.map((m, i) => ({ key: `m${i}`, keep: true, ...m })));
      } else {
        setTaskRows(res.tasks.map((t, i) => ({ key: `t${i}`, keep: true, ...t })));
      }
      setNotes(res.notes);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not get a suggestion.');
    } finally {
      setBusy(null);
    }
  }

  function close() {
    setMilestoneRows(null);
    setTaskRows(null);
    setNotes('');
    setError(null);
  }

  const kept = kind === 'milestones'
    ? (milestoneRows ?? []).filter((r) => r.keep)
    : (taskRows ?? []).filter((r) => r.keep);

  async function add() {
    // Refuse, never drop: a ticked row with no name is the agent's to fix.
    if (kept.some((r) => !r.title.trim())) {
      setError(`A ticked ${kind === 'milestones' ? 'milestone' : 'task'} has no name. Name it or untick it.`);
      return;
    }
    setBusy('adding');
    setError(null);
    try {
      await apiFetch(`/api/projects/${projectId}/plan`, {
        method: 'POST',
        json:
          kind === 'milestones'
            ? { milestones: (kept as MilestoneRow[]).map((r) => ({ title: r.title.trim(), milestoneDate: r.milestoneDate })) }
            : { tasks: (kept as TaskRow[]).map((r) => ({ title: r.title.trim(), milestoneId: r.milestoneId })) },
      });
      close();
      await onAdded();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add them.');
    } finally {
      setBusy(null);
    }
  }

  const label = kind === 'milestones' ? 'Suggest milestones' : 'Suggest tasks';

  if (!open) {
    return (
      <div>
        <Button variant="outline" onClick={() => void suggest()} disabled={busy !== null}>
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          {busy === 'suggesting' ? 'Reading the SOW…' : label}
        </Button>
        {kind === 'tasks' && milestones.length === 0 && (
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Tip: add milestones first, and suggested tasks are placed under them.
          </p>
        )}
        {error && <p className="mt-2 text-sm text-[var(--color-danger)]">{error}</p>}
      </div>
    );
  }

  const total = kind === 'milestones' ? milestoneRows!.length : taskRows!.length;

  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-t3-blue)]/40 bg-[var(--color-surface)] p-4">
      <h3 className="text-sm font-semibold text-[var(--color-header)]">
        Suggested {kind} from the SOW
      </h3>
      <p className="text-xs text-[var(--color-muted)]">
        Only ticked rows are added, and nothing is added until you press Add. The client sees these
        names on their portal.
      </p>
      {notes && (
        <p className="mt-3 rounded-[var(--radius-button)] bg-white px-3 py-2 text-sm text-[var(--color-body)]">
          {notes}
        </p>
      )}

      <div className="mt-3">
        {total === 0 ? (
          <p className="text-sm text-[var(--color-muted)]">Nothing to suggest - the project already covers the scope.</p>
        ) : kind === 'milestones' ? (
          <MilestoneReview rows={milestoneRows!} onChange={setMilestoneRows} />
        ) : (
          <TaskReview rows={taskRows!} milestones={milestones} onChange={setTaskRows} />
        )}
      </div>

      {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={() => void add()} disabled={busy !== null || kept.length === 0}>
          {busy === 'adding'
            ? 'Adding…'
            : `Add ${plural(kept.length, kind === 'milestones' ? 'milestone' : 'task')}`}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            if (window.confirm('Replace this suggestion, and any edits to it, with a new one?')) {
              void suggest();
            }
          }}
          disabled={busy !== null}
        >
          {busy === 'suggesting' ? 'Reading the SOW…' : 'Suggest again'}
        </Button>
        <Button variant="outline" onClick={close} disabled={busy !== null}>
          Discard
        </Button>
      </div>
    </div>
  );
}

function KeepBox({ checked, label, onChange }: { checked: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
      className="h-4 w-4 shrink-0 accent-[var(--color-t3-blue)]"
    />
  );
}

function MilestoneReview({
  rows,
  onChange,
}: {
  rows: MilestoneRow[];
  onChange: (rows: MilestoneRow[]) => void;
}): React.ReactElement {
  const set = (key: string, patch: Partial<MilestoneRow>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => (
        <li key={r.key} className={`flex items-center gap-3 ${r.keep ? '' : 'opacity-50'}`}>
          <KeepBox checked={r.keep} label={`Add milestone ${i + 1}`} onChange={(keep) => set(r.key, { keep })} />
          <input
            value={r.title}
            onChange={(e) => set(r.key, { title: e.target.value })}
            className={`${inputClass} min-w-0 flex-1`}
            aria-label={`Milestone ${i + 1} name`}
          />
          <input
            type="date"
            value={r.milestoneDate}
            onChange={(e) => e.target.value && set(r.key, { milestoneDate: e.target.value })}
            className={`${inputClass} w-40 shrink-0`}
            aria-label={`Milestone ${i + 1} date`}
          />
        </li>
      ))}
    </ul>
  );
}

function TaskReview({
  rows,
  milestones,
  onChange,
}: {
  rows: TaskRow[];
  milestones: ProjectMilestone[];
  onChange: (rows: TaskRow[]) => void;
}): React.ReactElement {
  const set = (key: string, patch: Partial<TaskRow>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  // Grouped under the milestone each task will go into, in the project's
  // milestone order, so what is being added where can be read at a glance.
  const groups = [
    ...milestones.map((m) => ({ id: m.id as string | null, title: m.title, date: m.milestoneDate })),
    { id: null, title: 'No milestone', date: null },
  ]
    .map((g) => ({ ...g, rows: rows.filter((r) => r.milestoneId === g.id) }))
    .filter((g) => g.rows.length > 0);

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.id ?? 'none'}>
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
            {g.title}
            {g.date && <span className="font-normal normal-case"> · due {g.date}</span>}
          </h4>
          <ul className="space-y-2">
            {g.rows.map((r) => (
              <li key={r.key} className={`flex items-center gap-3 ${r.keep ? '' : 'opacity-50'}`}>
                <KeepBox checked={r.keep} label={`Add task ${r.title}`} onChange={(keep) => set(r.key, { keep })} />
                <input
                  value={r.title}
                  onChange={(e) => set(r.key, { title: e.target.value })}
                  className={`${inputClass} min-w-0 flex-1`}
                  aria-label="Task name"
                />
                <select
                  value={r.milestoneId ?? ''}
                  onChange={(e) => set(r.key, { milestoneId: e.target.value || null })}
                  className={`${inputClass} w-48 shrink-0`}
                  aria-label={`Milestone for ${r.title}`}
                >
                  {milestones.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.title}
                    </option>
                  ))}
                  <option value="">No milestone</option>
                </select>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
